import React from 'react';

import {
  Stack, Box, Typography, CircularProgress, Alert, Divider,
  Table, TableHead, TableBody, TableRow, TableCell,
  TextField, Button, IconButton, Dropdown, MenuItem,
} from 'views/components';
import {DeleteOutline} from 'views/icons';
import {Translate, translate} from 'views/module';

import Backend from 'utils/backend';

// Years offered in the selector: last year through three years out.
const CURRENT_YEAR = new Date().getFullYear();
const YEAR_OPTIONS = [-1, 0, 1, 2, 3].map((offset) => CURRENT_YEAR + offset);

const Holidays = () => {
  const [year, setYear] = React.useState(CURRENT_YEAR);
  const [holidays, setHolidays] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);

  const [addDate, setAddDate] = React.useState('');
  const [addName, setAddName] = React.useState('');
  const [isAdding, setIsAdding] = React.useState(false);
  const [removingDate, setRemovingDate] = React.useState(null);

  const loadHolidays = React.useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await Backend.holiday.listHolidays(undefined, year);
      const sorted = (result.holidays || []).sort((a, b) => a.date.localeCompare(b.date));
      setHolidays(sorted);
    } catch (e) {
      setError(translate('client.ClientDetail.Payment.Holidays.LoadError'));
    } finally {
      setLoading(false);
    }
  }, [year]);

  React.useEffect(() => {
    loadHolidays();
  }, [loadHolidays]);

  const onAdd = async () => {
    if (!addDate || !addName) {
      return;
    }
    setIsAdding(true);
    setError(null);
    try {
      await Backend.holiday.addHoliday({date: addDate, name: addName});
      setAddDate('');
      setAddName('');
      await loadHolidays();
    } catch (e) {
      setError(translate('client.ClientDetail.Payment.Holidays.AddError'));
    } finally {
      setIsAdding(false);
    }
  };

  const onRemove = async (date) => {
    setRemovingDate(date);
    setError(null);
    try {
      await Backend.holiday.deleteHoliday(date);
      await loadHolidays();
    } catch (e) {
      setError(translate('client.ClientDetail.Payment.Holidays.RemoveError'));
    } finally {
      setRemovingDate(null);
    }
  };

  const sourceLabel = (source) => {
    if (source === 'MANUAL') {
      return translate('client.ClientDetail.Payment.Holidays.SourceManual');
    }
    return translate('client.ClientDetail.Payment.Holidays.SourceApiSync');
  };

  return (
    <Stack spacing={2} sx={{px: 3, py: 2}}>
      <Typography variant='body2' color='text.secondary'>
        <Translate expression='client.ClientDetail.Payment.Holidays.Description' />
      </Typography>

      <Box sx={{maxWidth: 160}}>
        <Dropdown
          label={translate('client.ClientDetail.Payment.Holidays.Year')}
          value={year}
          onChange={(value) => setYear(value)}
        >
          {
            YEAR_OPTIONS.map((option) => (
              <MenuItem key={option} value={option}>{option}</MenuItem>
            ))
          }
        </Dropdown>
      </Box>

      {
        error &&
        <Alert severity='error'>{error}</Alert>
      }

      {
        loading ?
        <Box sx={{display: 'flex', justifyContent: 'center', py: 3}}>
          <CircularProgress />
        </Box> :
        <Table size='small'>
          <TableHead>
            <TableRow>
              <TableCell><Translate expression='client.ClientDetail.Payment.Holidays.ColumnDate' /></TableCell>
              <TableCell><Translate expression='client.ClientDetail.Payment.Holidays.ColumnName' /></TableCell>
              <TableCell><Translate expression='client.ClientDetail.Payment.Holidays.ColumnSource' /></TableCell>
              <TableCell align='right' />
            </TableRow>
          </TableHead>
          <TableBody>
            {
              holidays.length === 0 &&
              <TableRow>
                <TableCell colSpan={4}>
                  <Typography variant='body2' color='text.secondary'>
                    <Translate
                      expression='client.ClientDetail.Payment.Holidays.Empty'
                      substitute={{year}}
                    />
                  </Typography>
                </TableCell>
              </TableRow>
            }
            {
              holidays.map((holiday) => (
                <TableRow key={holiday.date}>
                  <TableCell>{holiday.date}</TableCell>
                  <TableCell>{holiday.name}</TableCell>
                  <TableCell>{sourceLabel(holiday.source)}</TableCell>
                  <TableCell align='right'>
                    <IconButton
                      size='small'
                      color='error'
                      disabled={removingDate === holiday.date}
                      onClick={() => onRemove(holiday.date)}
                      aria-label={translate('client.ClientDetail.Payment.Holidays.Remove')}
                    >
                      <DeleteOutline fontSize='small' />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            }
          </TableBody>
        </Table>
      }

      <Divider />

      <Stack direction={{xs: 'column', sm: 'row'}} spacing={1} alignItems={{sm: 'center'}}>
        <TextField
          type='date'
          label={translate('client.ClientDetail.Payment.Holidays.AddDate')}
          value={addDate}
          onChange={setAddDate}
          InputLabelProps={{shrink: true}}
        />
        <TextField
          label={translate('client.ClientDetail.Payment.Holidays.AddName')}
          value={addName}
          onChange={setAddName}
        />
        <Button
          onClick={onAdd}
          loading={isAdding}
          disabled={!addDate || !addName}
        >
          <Translate expression='client.ClientDetail.Payment.Holidays.AddAction' />
        </Button>
      </Stack>
    </Stack>
  );
};

export default React.memo(Holidays);
